import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { authService } from '../services/api.service';
import { API_URL } from '../config';
import { Bar } from 'react-chartjs-2';
import { SubTabsContainer, SubTab } from './common/AdminTabs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

// Consistent admin page container styling
const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 20px;
  width: 100%;
  overflow-x: hidden;
`;

const Title = styled.h2`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  width: 100%;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  width: 100%;
`;

const StatCard = styled.div`
  background: #f9f9f9;
  border-radius: 6px;
  padding: 16px;
  border-left: 4px solid #4a90e2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0,0,0,0.15);
  }
`;

const StatTitle = styled.h3`
  font-size: 16px;
  color: #555;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const StatSubtext = styled.div`
  font-size: 13px;
  color: #777;
  margin-top: 4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  table-layout: fixed;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 8px;
  background-color: #f2f2f2;
  border-bottom: 2px solid #ddd;
  color: #555;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tr = styled.tr`
  &:hover {
    background-color: #f9f9f9;
  }
`;

const DateRangeSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
  background: #f5f7fa;
  padding: 15px;
  border-radius: 6px;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 150px;
`;

const Button = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3a80d2;
  }
`;

const ChartContainer = styled.div`
  height: 400px;
  margin-bottom: 30px;
  width: 100%;
  overflow-x: auto;
  background: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  box-shadow: inset 0 0 5px rgba(0,0,0,0.05);
`;

const ChartInner = styled.div`
  height: 100%;
  width: 100%;
  min-width: ${props => Math.max(props.dataPoints * 40, 400)}px;
  max-width: 100%;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #777;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  background-color: #fff0f0;
  color: #d32f2f;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border-left: 4px solid #d32f2f;
`;

const AIUsageStats = () => {
  const [systemStats, setSystemStats] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [totalRequestsCount, setTotalRequestsCount] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response.success && response.user) {
          setIsAdmin(response.user.role === 'admin');
          
          // Load stats for all users
          loadStats();
          
          // Always load daily stats for the current user
          loadDailyStats();
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Error loading user information.');
        setLoading(false);
      }
    };
    
    checkAdmin();
  }, []);
  
  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare options for the service call
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;
      
      console.log('Fetching AI usage stats with options:', options);
      
      // Try to use the service to get system stats
      let data;
      try {
        // First try to use the imported service
        const { aiUsageService } = await import('../services/ai-usage.service');
        data = await aiUsageService.getSystemStats(options);
      } catch (serviceError) {
        console.error('Error importing AI usage service:', serviceError);
        // Fallback to direct API call if service import fails
        const queryString = new URLSearchParams(options).toString();
        const url = `${API_URL}/ai-usage/system${queryString ? `?${queryString}` : ''}`;
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        data = await response.json();
      }
      
      if (data && data.success) {
        setSystemStats(data.stats || []);
      } else {
        throw new Error((data && data.message) || 'Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading AI usage statistics:', error);
      setError(error.message || 'Failed to connect to the server. Make sure the API server is running.');
      // Initialize with empty stats to avoid rendering errors
      setSystemStats([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadDailyStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare options for the service call
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;
      
      console.log('Fetching daily AI usage stats with options:', options);
      
      // Try to use the service to get daily stats
      let data;
      try {
        // First try to use the imported service
        const { aiUsageService } = await import('../services/ai-usage.service');
        data = await aiUsageService.getDailyUsageStats(options);
      } catch (serviceError) {
        console.error('Error importing AI usage service:', serviceError);
        // Fallback to direct API call if service import fails
        const queryString = new URLSearchParams(options).toString();
        const url = `${API_URL}/ai-usage/daily${queryString ? `?${queryString}` : ''}`;
        
        const token = localStorage.getItem('token');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        data = await response.json();
      }
      
      console.log('Daily AI usage stats:', data);
      
      if (data.success) {
        setDailyStats(data.dailyStats || []);
        setTotalTokensUsed(data.totalTokens || 0);
        setTotalRequestsCount(data.totalRequests || 0);
      } else {
        throw new Error(data.message || 'Failed to load daily AI usage statistics');
      }
    } catch (error) {
      console.error('Error loading daily AI usage stats:', error);
      setError(error.message || 'Failed to connect to the server. Make sure the API server is running.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateFilterApply = () => {
    loadStats();
    loadDailyStats();
  };
  
  // Calculate totals
  const totalRequests = systemStats.reduce((sum, stat) => sum + parseInt(stat.request_count), 0);
  const totalTokens = systemStats.reduce((sum, stat) => sum + parseInt(stat.total_tokens || 0), 0);
  const uniqueUsers = systemStats.reduce((sum, stat) => sum + parseInt(stat.unique_users), 0);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Prepare data for the bar chart
  const prepareChartData = () => {
    if (!dailyStats || dailyStats.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    // Sort by date
    const sortedStats = [...dailyStats].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Extract dates and tokens
    const dates = sortedStats.map(stat => new Date(stat.date).toLocaleDateString());
    
    // Group by provider
    const providerMap = {};
    
    sortedStats.forEach(stat => {
      const provider = stat.ai_provider || 'unknown';
      if (!providerMap[provider]) {
        providerMap[provider] = Array(dates.length).fill(0);
      }
      
      const dateIndex = dates.indexOf(new Date(stat.date).toLocaleDateString());
      if (dateIndex !== -1) {
        // Use request_count instead of tokens for the chart
        providerMap[provider][dateIndex] = parseInt(stat.request_count) || 0;
      }
    });
    
    // Create datasets for each provider
    const datasets = Object.keys(providerMap).map((provider, index) => {
      // Generate a color based on the provider name
      const colors = [
        'rgba(54, 162, 235, 0.6)', // blue
        'rgba(255, 99, 132, 0.6)',  // red
        'rgba(75, 192, 192, 0.6)',  // green
        'rgba(255, 159, 64, 0.6)',  // orange
        'rgba(153, 102, 255, 0.6)'  // purple
      ];
      
      return {
        label: provider,
        data: providerMap[provider],
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length].replace('0.6', '1'),
        borderWidth: 1
      };
    });
    
    return {
      labels: dates,
      datasets
    };
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily AI Usage (Requests)'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.raw.toLocaleString()} requests`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Requests'
        },
        beginAtZero: true
      }
    }
  };
  
  if (!isAdmin) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>You must be an administrator to view AI usage statistics.</ErrorMessage>
        </Container>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <Container>
        <Title>AI Model Usage Statistics</Title>
        
        <SubTabsContainer>
          <SubTab 
            active={activeSubTab === 'overview'} 
            onClick={() => setActiveSubTab('overview')}
          >
            Overview
          </SubTab>
          <SubTab 
            active={activeSubTab === 'daily'} 
            onClick={() => setActiveSubTab('daily')}
          >
            Daily Usage
          </SubTab>
          {isAdmin && (
            <SubTab 
              active={activeSubTab === 'details'} 
              onClick={() => setActiveSubTab('details')}
            >
              Detailed Stats
            </SubTab>
          )}
        </SubTabsContainer>
        
        <DateRangeSelector>
          <div>
            <label htmlFor="start-date">Start Date: </label>
            <DateInput 
              type="date" 
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="end-date">End Date: </label>
            <DateInput 
              type="date" 
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <Button onClick={handleDateFilterApply}>Apply Filter</Button>
        </DateRangeSelector>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {loading ? (
          <LoadingMessage>Loading AI usage statistics...</LoadingMessage>
        ) : (
          <>
            {activeSubTab === 'overview' && (
              <StatsGrid>
                <StatCard>
                  <StatTitle>Total Requests</StatTitle>
                  <StatValue>{totalRequestsCount.toLocaleString()}</StatValue>
                  <StatSubtext>Across all AI models</StatSubtext>
                </StatCard>
                {isAdmin && (
                  <>
                    <StatCard>
                      <StatTitle>Unique Users</StatTitle>
                      <StatValue>{uniqueUsers.toLocaleString()}</StatValue>
                      <StatSubtext>Using AI models</StatSubtext>
                    </StatCard>
                  </>
                )}
              </StatsGrid>
            )}
            
            {activeSubTab === 'daily' && (
              <>
                <ChartContainer>
                  {dailyStats.length > 0 ? (
                    <ChartInner dataPoints={dailyStats.length}>
                      <Bar data={prepareChartData()} options={chartOptions} />
                    </ChartInner>
                  ) : (
                    <p>No daily usage data available for the selected time period.</p>
                  )}
                </ChartContainer>
              </>
            )}
            
            {isAdmin && activeSubTab === 'details' && (
              <>
                {systemStats.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Provider</Th>
                          <Th>Model</Th>
                          <Th>Requests</Th>
                          <Th>User</Th>
                          <Th>Total Tokens</Th>
                          <Th>Avg. Tokens/Request</Th>
                          <Th>First Used</Th>
                          <Th>Last Used</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemStats.map((stat, index) => (
                          <Tr key={index}>
                            <Td>{stat.ai_provider}</Td>
                            <Td>{stat.ai_model}</Td>
                            <Td>{parseInt(stat.request_count).toLocaleString()}</Td>
                            <Td>{stat.user_email || stat.user_id}</Td>
                            <Td>{parseInt(stat.total_tokens || 0).toLocaleString()}</Td>
                            <Td>{Math.round(parseFloat(stat.avg_tokens_per_request || 0)).toLocaleString()}</Td>
                            <Td>{formatDate(stat.first_usage)}</Td>
                            <Td>{formatDate(stat.last_usage)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p>No usage data available for the selected time period.</p>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
};

export default AIUsageStats;
